import React, { useState } from 'react';
import './OPACInterface.css';

const OPACInterface = ({ books, onBookSelect, onSearch, searchQuery }) => {
  const [searchType, setSearchType] = useState('keyword'); // 'keyword', 'title', 'author', 'subject', 'isbn'
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [filter, setFilter] = useState('all'); // 'all', 'available', 'new', 'popular'
  const [sortBy, setSortBy] = useState('relevance'); // 'relevance', 'title', 'author', 'date', 'popularity'
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(localSearchQuery);
  };

  const getStatusText = (book) => {
    switch (book.status) {
      case 'available':
        return '可借';
      case 'borrowed':
        return '已借出';
      case 'overdue':
        return '逾期';
      case 'reserved':
        return '已預約';
      case 'maintenance':
        return '維護中';
      default:
        return '未知';
    }
  };

  const getStatusClass = (book) => {
    switch (book.status) {
      case 'available':
        return 'status-available';
      case 'borrowed':
        return 'status-borrowed';
      case 'overdue':
        return 'status-overdue';
      case 'reserved':
        return 'status-reserved';
      case 'maintenance':
        return 'status-maintenance';
      default:
        return 'status-unknown';
    }
  };

  const isNewBook = (book) => {
    const addedDate = new Date(book.addedDate);
    const today = new Date();
    const diffTime = today - addedDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30; // Books added within 30 days are considered new
  };

  const filteredAndSortedBooks = books
    .filter(book => {
      // Filter by status
      if (filter === 'available' && book.status !== 'available') return false;
      if (filter === 'new' && !isNewBook(book)) return false;

      // Filter by search query
      if (localSearchQuery) {
        const query = localSearchQuery.toLowerCase();
        
        switch (searchType) {
          case 'title':
            return book.title.toLowerCase().includes(query) || 
                   (book.titleEn && book.titleEn.toLowerCase().includes(query));
          case 'author':
            return book.author.toLowerCase().includes(query) || 
                   (book.authorEn && book.authorEn.toLowerCase().includes(query));
          case 'subject':
            return book.subject.toLowerCase().includes(query);
          case 'isbn':
            return book.isbn.includes(query);
          case 'keyword':
          default:
            return (
              book.title.toLowerCase().includes(query) ||
              (book.titleEn && book.titleEn.toLowerCase().includes(query)) ||
              book.author.toLowerCase().includes(query) ||
              (book.authorEn && book.authorEn.toLowerCase().includes(query)) ||
              book.subject.toLowerCase().includes(query) ||
              book.isbn.includes(query) ||
              book.callNumber.toLowerCase().includes(query) ||
              book.description.toLowerCase().includes(query)
            );
        }
      }

      return true;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'author':
          aValue = a.author.toLowerCase();
          bValue = b.author.toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.addedDate);
          bValue = new Date(b.addedDate);
          break;
        case 'popularity':
          // In a real app, this would be based on borrowing statistics
          aValue = Math.random(); // Placeholder
          bValue = Math.random(); // Placeholder
          break;
        case 'relevance':
        default:
          // Relevance would be calculated based on search match quality
          return 0;
      }

      if (sortBy === 'date') {
        return bValue - aValue; // Newest first
      } else {
        return aValue > bValue ? 1 : -1;
      }
    });

  const renderGridView = () => (
    <div className="books-grid">
      {filteredAndSortedBooks.map((book) => (
        <div key={book.id} className="book-card" onClick={() => onBookSelect(book)}>
          <div className="book-cover">
            {book.coverUrl ? (
              <img src={book.coverUrl} alt={book.title} />
            ) : (
              <div className="cover-placeholder">
                <span className="cover-icon">📚</span>
                <span className="cover-title">{book.title}</span>
              </div>
            )}
            <div className="book-status">
              <span className={`status-badge ${getStatusClass(book)}`}>
                {getStatusText(book)}
              </span>
              {isNewBook(book) && <span className="new-badge">新書</span>}
            </div>
          </div>
          <div className="book-info">
            <h3 className="book-title">{book.title}</h3>
            {book.titleEn && (
              <h4 className="book-title-en">{book.titleEn}</h4>
            )}
            <p className="book-author">{book.author}</p>
            <p className="book-subject">{book.subject}</p>
            <p className="book-call-number">索書號: {book.callNumber}</p>
            <div className="book-meta">
              <span className="publish-year">{book.publishYear}</span>
              <span className="format">{book.format}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="books-list">
      <table>
        <thead>
          <tr>
            <th>書名</th>
            <th>作者</th>
            <th>主題</th>
            <th>索書號</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedBooks.map((book) => (
            <tr key={book.id} className="book-row">
              <td className="book-title">
                <div className="title-content">
                  <span className="title-zh">{book.title}</span>
                  {book.titleEn && (
                    <span className="title-en">{book.titleEn}</span>
                  )}
                  {isNewBook(book) && <span className="new-badge">新書</span>}
                </div>
              </td>
              <td className="book-author">{book.author}</td>
              <td className="book-subject">{book.subject}</td>
              <td className="book-call-number">{book.callNumber}</td>
              <td>
                <span className={`status-badge ${getStatusClass(book)}`}>
                  {getStatusText(book)}
                </span>
              </td>
              <td className="actions">
                <button
                  className="view-button"
                  onClick={() => onBookSelect(book)}
                >
                  查看詳情
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="opac-interface">
      <div className="opac-header">
        <h2>線上圖書館目錄 (OPAC)</h2>
        <p>歡迎使用圖書館線上查詢系統</p>
      </div>

      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-controls">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="search-type-select"
            >
              <option value="keyword">關鍵字搜尋</option>
              <option value="title">書名搜尋</option>
              <option value="author">作者搜尋</option>
              <option value="subject">主題搜尋</option>
              <option value="isbn">ISBN搜尋</option>
            </select>
            <input
              type="text"
              placeholder="請輸入搜尋關鍵字..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              搜尋
            </button>
          </div>
        </form>
      </div>

      <div className="opac-controls">
        <div className="filter-controls">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">全部圖書</option>
            <option value="available">可借圖書</option>
            <option value="new">新書</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="relevance">相關性排序</option>
            <option value="title">按書名排序</option>
            <option value="author">按作者排序</option>
            <option value="date">按新增日期排序</option>
            <option value="popularity">按熱門程度排序</option>
          </select>
        </div>

        <div className="view-controls">
          <button
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            📱 網格檢視
          </button>
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            📋 列表檢視
          </button>
        </div>
      </div>

      <div className="search-results">
        <div className="results-header">
          <h3>搜尋結果</h3>
          <span className="results-count">
            找到 {filteredAndSortedBooks.length} 本圖書
          </span>
        </div>

        {filteredAndSortedBooks.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h4>沒有找到符合條件的圖書</h4>
            <p>請嘗試使用不同的關鍵字或調整搜尋條件</p>
            <div className="search-tips">
              <h5>搜尋小貼士:</h5>
              <ul>
                <li>使用書名的部分關鍵字</li>
                <li>嘗試作者的中文或英文姓名</li>
                <li>使用主題或學科名稱</li>
                <li>檢查ISBN號碼是否正確</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="results-content">
            {viewMode === 'grid' ? renderGridView() : renderListView()}
          </div>
        )}
      </div>

      <div className="opac-footer">
        <div className="quick-links">
          <h4>快速連結</h4>
          <div className="links-grid">
            <a href="#" className="quick-link">新書推薦</a>
            <a href="#" className="quick-link">熱門圖書</a>
            <a href="#" className="quick-link">主題書單</a>
            <a href="#" className="quick-link">借閱規則</a>
            <a href="#" className="quick-link">開放時間</a>
            <a href="#" className="quick-link">聯絡我們</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OPACInterface;
